// src/newDashboard/admin/AdminClaimed.jsx
//
// Admin-only "Claimed bars" page: every bar an owner or charity has
// claimed, which email claimed it, and when that account last logged in.
// Unclaim one bar, or check several and unclaim them together. Unclaiming
// keeps the bar's specials, events and photos; it just frees the bar so the
// real owner can claim it.

import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../firebase";
import PrintBarQr from "../shared/PrintBarQr";

// One-time free texting credit for a pilot bar (max $25, once per bar).
const TEXTS_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/barTexts";
async function giveFreeTexts(barId, barName) {
  const raw = window.prompt(`Free texting credit for ${barName}, in dollars (one time only, max $25). $10 = 200 texts.`, "10");
  if (raw === null) return;
  const amount = Number(String(raw).replace(/[^0-9.]/g, ""));
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(TEXTS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action: "grant", barId, amount }),
  });
  const data = await res.json().catch(() => ({}));
  window.alert(res.ok ? `Done. ${barName} now has $${(data.balanceCents / 100).toFixed(2)} of texting credit.` : data.error || "Something went wrong.");
}

const URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/claimedBars";

async function post(body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const box = { background: "#17131F", borderRadius: "14px", padding: "18px", marginBottom: "14px" };
const muted = { color: "#B8B0C8", fontSize: "15px", lineHeight: 1.5 };
const btn = {
  minHeight: "40px", padding: "0 14px", borderRadius: "8px", border: "none",
  font: "700 14px Inter, sans-serif", cursor: "pointer", marginRight: "8px",
};
const pink = { ...btn, background: "#FF3D81", color: "#fff" };
const gray = { ...btn, background: "#3B2F5C", color: "#fff" };
const input = { padding: "10px", borderRadius: "8px", border: "none", fontSize: "15px", minWidth: "260px" };

const keyOf = (r) => `${r.barId}|${r.role}`;

function when(iso) {
  if (!iso) return "never";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminClaimed() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [picked, setPicked] = useState(() => new Set());
  const [working, setWorking] = useState(false);
  const [loginInfo, setLoginInfo] = useState(true);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await post({ action: "list" });
      setRows(data.rows || []);
      setLoginInfo(data.loginInfo !== false);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (roleFilter !== "all" && r.role !== roleFilter) return false;
      if (!q) return true;
      return [r.name, r.city, r.state, r.email, r.userName].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, search, roleFilter]);

  // How many bars each email has, so you can spot one account with many.
  const perEmail = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = r.email || r.uid;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [rows]);

  const toggle = (r) => {
    setPicked((prev) => {
      const next = new Set(prev);
      const k = keyOf(r);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const pickAllShown = () => setPicked(new Set(shown.map(keyOf)));
  const pickNone = () => setPicked(new Set());

  const doUnclaim = async (list) => {
    if (!list.length) return;
    const paying = list.filter((r) => r.paying);
    const names = list.slice(0, 8).map((r) => `• ${r.name} (${r.email || "no email"})`).join("\n");
    const more = list.length > 8 ? `\n…and ${list.length - 8} more` : "";
    const warn = paying.length
      ? `\n\n⚠️ ${paying.length} of these are paying. Unclaiming does NOT stop their Stripe payment. Cancel it in Stripe too.`
      : "";
    if (!window.confirm(`Unclaim ${list.length} bar${list.length === 1 ? "" : "s"}?\n\n${names}${more}${warn}\n\nThe bars stay in the app with their specials and events. Anyone can claim them again.`)) {
      return;
    }
    setWorking(true);
    setMsg("");
    setError("");
    try {
      let total = 0;
      for (let i = 0; i < list.length; i += 100) {
        const chunk = list.slice(i, i + 100).map((r) => ({ barId: r.barId, role: r.role }));
        const out = await post({ action: "unclaim", items: chunk });
        total += out.unclaimed || 0;
      }
      setMsg(`Done. ${total} bar${total === 1 ? "" : "s"} unclaimed.`);
      setPicked(new Set());
      await load();
    } catch (e) {
      setError(`Unclaiming stopped: ${e.message}`);
    }
    setWorking(false);
  };

  const pickedRows = rows.filter((r) => picked.has(keyOf(r)));

  return (
    <div style={{ color: "#fff", maxWidth: "980px" }}>
      <h2 style={{ marginBottom: "6px" }}>Claimed bars</h2>
      <p style={{ ...muted, marginBottom: "16px" }}>
        Every bar an owner or charity has claimed, and the email that claimed it. Use this when someone claimed a bar
        by mistake or can't log in. <strong>Unclaim</strong> frees the bar so the real owner can claim it. Its specials,
        events and photos stay. "Last login: never" or a very old date usually means a test or mistake account.
      </p>

      {error && <div style={{ ...box, background: "#3A1420", color: "#FFC2D1" }}>{error}</div>}
      {msg && <div style={{ ...box, background: "#12301F", color: "#4ADE80" }}>{msg}</div>}

      <div style={box}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
          <input
            style={input}
            placeholder="Search bar name, city, email or person"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select style={{ ...input, minWidth: 0 }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">Owners and charities</option>
            <option value="owner">Owners only</option>
            <option value="charity">Charities only</option>
          </select>
          <button style={gray} onClick={load} disabled={loading || working}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <p style={{ ...muted, margin: "12px 0 0" }}>
          {rows.length.toLocaleString()} claimed in all · showing {shown.length.toLocaleString()}
        </p>
        <div style={{ marginTop: "10px" }}>
          <button style={gray} onClick={pickAllShown} disabled={!shown.length || working}>
            Check all shown
          </button>
          <button style={gray} onClick={pickNone} disabled={!picked.size || working}>
            Uncheck all
          </button>
          <button style={pink} onClick={() => doUnclaim(pickedRows)} disabled={!pickedRows.length || working}>
            {working ? "Unclaiming…" : `Unclaim checked (${pickedRows.length})`}
          </button>
        </div>
      </div>

      {loading ? (
        <p style={muted}>Loading claimed bars…</p>
      ) : shown.length === 0 ? (
        <p style={muted}>{rows.length ? "Nothing matches that search." : "No bars are claimed yet."}</p>
      ) : (
        shown.map((r) => {
          const k = keyOf(r);
          const count = perEmail[r.email || r.uid] || 1;
          return (
            <div key={k} style={{ ...box, display: "flex", gap: "14px", alignItems: "flex-start" }}>
              <input
                type="checkbox"
                checked={picked.has(k)}
                onChange={() => toggle(r)}
                style={{ width: "20px", height: "20px", marginTop: "4px" }}
                aria-label={`Check ${r.name}`}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "18px", fontWeight: 700 }}>{r.name}</div>
                <div style={muted}>
                  {[r.city, r.state].filter(Boolean).join(", ") || "No city on file"} ·{" "}
                  <span style={{ color: r.role === "owner" ? "#FF6FA3" : "#B9A6FF", fontWeight: 600 }}>
                    {r.role === "owner" ? "Owner" : "Charity"}
                  </span>
                </div>
                <div style={{ ...muted, marginTop: "4px" }}>
                  Claimed by <strong style={{ color: "#fff" }}>{r.email || "(no email)"}</strong>
                  {r.userName ? ` · ${r.userName}` : ""}
                  {count > 1 ? ` · has ${count} bars` : ""}
                </div>
                <div style={muted}>
                  {r.accountMissing
                    ? "⚠️ This account was deleted. Safe to unclaim."
                    : loginInfo
                    ? `Last login: ${when(r.lastLogin)} · Account made: ${when(r.accountMade)}`
                    : ""}
                </div>
                {r.paying && (
                  <div style={{ marginTop: "6px", display: "inline-block", padding: "2px 8px", borderRadius: "6px", background: "#FBBF24", color: "#0B0B12", fontSize: "13px", fontWeight: 800 }}>
                    PAYING: {r.paying} · cancel in Stripe too if you unclaim
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
                <PrintBarQr barId={r.barId} barName={r.name} />
                <button style={gray} onClick={() => giveFreeTexts(r.barId, r.name)} disabled={working}>
                  Free texts
                </button>
                <button style={gray} onClick={() => doUnclaim([r])} disabled={working}>
                  Unclaim
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
